import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList, Search, CheckCircle, XCircle, Clock, Archive,
  Eye, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, RefreshCw,
  Calendar, Users, Building2, AlertCircle, PlayCircle, Check, Ban,
  FileText, X, MessageSquare, Shield, ExternalLink, Download, Mail, Phone
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/apiService';

const API_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_URL}${url}`;
};

const getFileType = (url) => {
  if (!url) return 'unknown';
  const lower = url.toLowerCase();
  if (lower.includes('.pdf')) return 'pdf';
  if (lower.includes('.png') || lower.includes('.jpg') || lower.includes('.jpeg') || lower.includes('.gif') || lower.includes('.webp')) return 'image';
  return 'unknown';
};

const OEMWorkPermits = () => {
  const navigate = useNavigate();

  // Status tab state
  const [activeStatus, setActiveStatus] = useState('pending');

  // Data states
  const [workPermits, setWorkPermits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  // Stats
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    in_progress: 0,
    completed: 0,
    cancelled: 0,
    expired: 0
  });

  // Action modals
  const [actionModal, setActionModal] = useState({ show: false, type: null, permit: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [oemComments, setOemComments] = useState('');

  // Detail modal
  const [detailModal, setDetailModal] = useState({ show: false, permit: null });
  const [detailLoading, setDetailLoading] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userNominations, setUserNominations] = useState({});
  const [loadingNominations, setLoadingNominations] = useState({});

  // Document preview for detail modal
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [userQualifications, setUserQualifications] = useState({});
  const [loadingQualifications, setLoadingQualifications] = useState({});
  const [previewDocument, setPreviewDocument] = useState(null);

  // Status configuration
  const statusConfig = {
    pending: {
      label: 'Pending',
      icon: Clock,
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      dot: 'bg-amber-500',
      badge: 'bg-amber-100 text-amber-600'
    },
    approved: {
      label: 'Approved',
      icon: CheckCircle,
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      dot: 'bg-emerald-500',
      badge: 'bg-emerald-100 text-emerald-600'
    },
    rejected: {
      label: 'Rejected',
      icon: XCircle,
      bg: 'bg-red-50',
      text: 'text-red-700',
      dot: 'bg-red-500',
      badge: 'bg-red-100 text-red-600'
    },
    in_progress: {
      label: 'In Progress',
      icon: PlayCircle,
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      dot: 'bg-blue-500',
      badge: 'bg-blue-100 text-blue-600'
    },
    completed: {
      label: 'Completed',
      icon: Check,
      bg: 'bg-violet-50',
      text: 'text-violet-700',
      dot: 'bg-violet-500',
      badge: 'bg-violet-100 text-violet-600'
    },
    cancelled: {
      label: 'Cancelled',
      icon: Ban,
      bg: 'bg-slate-50',
      text: 'text-slate-600',
      dot: 'bg-slate-400',
      badge: 'bg-slate-100 text-slate-600'
    },
    expired: {
      label: 'Expired',
      icon: AlertCircle,
      bg: 'bg-orange-50',
      text: 'text-orange-600',
      dot: 'bg-orange-400',
      badge: 'bg-orange-100 text-orange-600'
    }
  };

  // Fetch work permits for all statuses to get counts
  const fetchAllStats = async () => {
    try {
      const response = await api.get('/oem/work-permits/stats/');
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      // Fallback: fetch each status individually
      const statuses = ['pending', 'approved', 'rejected', 'in_progress', 'completed', 'cancelled', 'expired'];
      const newStats = {};
      for (const status of statuses) {
        try {
          const res = await api.get(`/oem/work-permits/?status=${status}`);
          newStats[status] = Array.isArray(res.data) ? res.data.length : 0;
        } catch {
          newStats[status] = 0;
        }
      }
      setStats(newStats);
    }
  };

  // Fetch work permits for active status
  const fetchWorkPermits = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/oem/work-permits/?status=${activeStatus}`);
      setWorkPermits(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching work permits:', err);
      setError('Failed to load work permits');
      setWorkPermits([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllStats();
  }, []);

  useEffect(() => {
    fetchWorkPermits();
    setCurrentPage(1);
  }, [activeStatus]);

  // Format date/time
  const formatDateTime = (dateTime) => {
    if (!dateTime) return '-';
    return new Date(dateTime).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get team leader name
  const getRequesterName = (workPermit) => {
    if (workPermit.team_data?.team_leader_name) {
      return workPermit.team_data.team_leader_name;
    }
    return '-';
  };

  // Filter work permits
  const filteredWorkPermits = useMemo(() => {
    if (!searchTerm) return workPermits;

    const term = searchTerm.toLowerCase();
    return workPermits.filter(wp => {
      const requesterName = getRequesterName(wp).toLowerCase();
      return (
        (wp.work_permit_number || '').toLowerCase().includes(term) ||
        (wp.requesting_company_name || '').toLowerCase().includes(term) ||
        (wp.project_name || '').toLowerCase().includes(term) ||
        (wp.turbine_name || '').toLowerCase().includes(term) ||
        (wp.work_scope_name || '').toLowerCase().includes(term) ||
        requesterName.includes(term)
      );
    });
  }, [workPermits, searchTerm]);

  // Sort work permits
  const sortedWorkPermits = useMemo(() => {
    const sorted = [...filteredWorkPermits];

    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'work_permit_number':
          aValue = a.work_permit_number || '';
          bValue = b.work_permit_number || '';
          break;
        case 'project':
          aValue = a.project_name || '';
          bValue = b.project_name || '';
          break;
        case 'turbine':
          aValue = a.turbine_name || '';
          bValue = b.turbine_name || '';
          break;
        case 'company':
          aValue = a.requesting_company_name || '';
          bValue = b.requesting_company_name || '';
          break;
        case 'work_scope':
          aValue = a.work_scope || '';
          bValue = b.work_scope || '';
          break;
        case 'planned_end':
          aValue = a.planned_end ? new Date(a.planned_end) : null;
          bValue = b.planned_end ? new Date(b.planned_end) : null;
          if (!aValue && !bValue) return 0;
          if (!aValue) return 1;
          if (!bValue) return -1;
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        case 'created_at':
        case 'approved_at':
        case 'rejected_at':
          aValue = a[sortConfig.key] ? new Date(a[sortConfig.key]) : null;
          bValue = b[sortConfig.key] ? new Date(b[sortConfig.key]) : null;
          if (!aValue && !bValue) return 0;
          if (!aValue) return 1;
          if (!bValue) return -1;
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        default:
          aValue = '';
          bValue = '';
      }

      if (typeof aValue === 'string') {
        if (!aValue && !bValue) return 0;
        if (!aValue) return 1;
        if (!bValue) return -1;
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return 0;
    });

    return sorted;
  }, [filteredWorkPermits, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedWorkPermits.length / itemsPerPage) || 1;
  const paginatedWorkPermits = sortedWorkPermits.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getVisiblePages = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  // Sorting handler
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1);
  };

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  // View work permit details - open modal instead of navigating
  const handleViewWorkPermit = async (workPermit) => {
    setDetailModal({ show: true, permit: workPermit });
    setDetailLoading(true);
    try {
      // Fetch fresh data for the work permit
      const response = await api.get(`/oem/work-permits/${workPermit.id}/`);
      setDetailModal({ show: true, permit: response.data });
    } catch (err) {
      console.error('Error fetching work permit details:', err);
      // Keep the existing data if fetch fails
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailModal = () => {
    setDetailModal({ show: false, permit: null });
    setExpandedUser(null);
    setUserNominations({});
    setLoadingNominations({});
    setSelectedTeamMember(null);
    setUserQualifications({});
    setLoadingQualifications({});
    setPreviewDocument(null);
  };

  // Fetch nominations for a team member
  const fetchUserNominations = async (userId) => {
    if (userNominations[userId] || loadingNominations[userId]) return;

    setLoadingNominations(prev => ({ ...prev, [userId]: true }));
    try {
      const response = await api.get(`/oem/nominations/?user_id=${userId}&status=approved`);
      setUserNominations(prev => ({ ...prev, [userId]: response.data || [] }));
    } catch (err) {
      console.error('Error fetching user nominations:', err);
      setUserNominations(prev => ({ ...prev, [userId]: [] }));
    } finally {
      setLoadingNominations(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Fetch qualifications for a team member
  const fetchUserQualifications = async (userId) => {
    if (userQualifications[userId] || loadingQualifications[userId]) return;

    setLoadingQualifications(prev => ({ ...prev, [userId]: true }));
    try {
      const response = await api.get(`/userqualification/?user=${userId}`);
      // Handle both paginated and non-paginated responses
      const data = response.data?.results || response.data || [];
      console.log('User qualifications for', userId, ':', data);
      setUserQualifications(prev => ({ ...prev, [userId]: Array.isArray(data) ? data : [] }));
    } catch (err) {
      console.error('Error fetching user qualifications:', err);
      setUserQualifications(prev => ({ ...prev, [userId]: [] }));
    } finally {
      setLoadingQualifications(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Select team member and fetch their data
  const handleSelectTeamMember = (member) => {
    setSelectedTeamMember(member);
    setPreviewDocument(null);
    fetchUserNominations(member.id);
    fetchUserQualifications(member.id);
  };

  // Handle document preview
  const handlePreview = (url, name) => {
    if (!url) return;
    setPreviewDocument({ url: getImageUrl(url), name, type: getFileType(url) });
  };

  // Get document status
  const getDocStatus = (doc) => {
    if (doc.is_expired) return { status: 'expired', color: 'red', label: 'EXPIRED' };
    if (doc.expiry_date) {
      const daysUntil = Math.ceil((new Date(doc.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
      if (daysUntil <= 30) return { status: 'expiring', color: 'amber', label: 'EXPIRING SOON' };
    }
    return { status: 'valid', color: 'emerald', label: 'VALID' };
  };

  // Toggle user expansion and fetch nominations
  const toggleUserExpansion = (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
    } else {
      setExpandedUser(userId);
      fetchUserNominations(userId);
    }
  };

  // Get user role in work permit
  const getUserRole = (userId, teamData) => {
    if (!teamData) return 'Team Member';
    if (userId === teamData.team_leader) return 'Team Leader';
    if (userId === teamData.supervisor) return 'Supervisor';
    return 'Team Member';
  };

  // Check if nomination is valid for the work permit
  const isNominationValidForPermit = (nomination, workPermit) => {
    if (!nomination || !workPermit) return false;

    // Check status
    if (nomination.status !== 'approved') return false;

    // Check date validity (OEM nominations use date_start/date_end)
    const now = new Date();
    const startDate = nomination.date_start ? new Date(nomination.date_start) : null;
    const endDate = nomination.date_end ? new Date(nomination.date_end) : null;

    if (startDate && now < startDate) return false;
    if (endDate && now > endDate) return false;

    // Check if nomination's authorization levels match (if work permit has auth level requirement)
    // For now, just check dates - can be extended later

    return true;
  };

  // Get valid nominations for a user for this work permit
  const getValidNominations = (userId, workPermit) => {
    const nominations = userNominations[userId] || [];
    return nominations.filter(nom => isNominationValidForPermit(nom, workPermit));
  };

  // Action handlers
  const openActionModal = (type, permit) => {
    setActionModal({ show: true, type, permit });
    setRejectReason('');
    setOemComments('');
  };

  const closeActionModal = () => {
    setActionModal({ show: false, type: null, permit: null });
    setRejectReason('');
    setOemComments('');
  };

  const handleApprove = async () => {
    if (!actionModal.permit) return;
    setActionLoading(true);
    try {
      await api.post(`/oem/work-permits/${actionModal.permit.id}/approve/`, {
        oem_comments: oemComments
      });
      toast.success('Work permit approved successfully');
      closeActionModal();
      fetchWorkPermits();
      fetchAllStats();
    } catch (err) {
      console.error('Error approving work permit:', err);
      toast.error(err.response?.data?.error || 'Failed to approve work permit');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!actionModal.permit || !rejectReason.trim()) return;
    setActionLoading(true);
    try {
      await api.post(`/oem/work-permits/${actionModal.permit.id}/reject/`, {
        rejection_reason: rejectReason,
        oem_comments: oemComments
      });
      toast.success('Work permit rejected');
      closeActionModal();
      fetchWorkPermits();
      fetchAllStats();
    } catch (err) {
      console.error('Error rejecting work permit:', err);
      toast.error(err.response?.data?.error || 'Failed to reject work permit');
    } finally {
      setActionLoading(false);
    }
  };

  // Get status-specific date column
  const getStatusDateColumn = () => {
    switch (activeStatus) {
      case 'pending':
        return { key: 'created_at', label: 'Submitted' };
      case 'approved':
      case 'in_progress':
        return { key: 'approved_at', label: 'Approved' };
      case 'rejected':
        return { key: 'rejected_at', label: 'Rejected' };
      case 'completed':
        return { key: 'actual_end', label: 'Completed' };
      case 'cancelled':
      case 'expired':
        return { key: 'updated_at', label: 'Updated' };
      default:
        return { key: 'created_at', label: 'Date' };
    }
  };

  const statusDateColumn = getStatusDateColumn();
  const config = statusConfig[activeStatus];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5 font-sans">
      {/* Header */}
      <div className="flex items-center justify-end">
        <button
          onClick={() => { fetchAllStats(); fetchWorkPermits(); }}
          className="h-9 px-4 bg-white text-slate-600 rounded-xl border border-orange-400 hover:bg-orange-50 flex items-center gap-1.5 font-mono font-medium uppercase tracking-wider text-[10px] transition-colors"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {/* Status Tabs */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-1.5">
        <div className="flex gap-1 flex-wrap">
          {Object.entries(statusConfig).map(([status, cfg]) => {
            const Icon = cfg.icon;
            const isActive = activeStatus === status;
            return (
              <button
                key={status}
                onClick={() => setActiveStatus(status)}
                className={`flex-1 min-w-[100px] flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl font-mono font-medium text-[11px] uppercase tracking-wide transition-all ${
                  isActive
                    ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{cfg.label}</span>
                <span className={`font-mono px-1.5 py-0.5 rounded-md text-[10px] ${
                  isActive ? 'bg-white/20 text-white' : cfg.badge
                }`}>
                  {stats[status] || 0}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {/* Search Bar */}
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by permit number, company, project, turbine, scope, requester..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full pl-10 pr-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-300 transition-colors"
              />
            </div>
            <select
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              className="h-10 pl-3 pr-8 text-xs font-bold bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-300"
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('work_permit_number')}
                    className="flex items-center font-mono text-[10px] font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700"
                  >
                    Permit No. {renderSortIndicator('work_permit_number')}
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('project')}
                    className="flex items-center font-mono text-[10px] font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700"
                  >
                    Project {renderSortIndicator('project')}
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('turbine')}
                    className="flex items-center font-mono text-[10px] font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700"
                  >
                    Turbine {renderSortIndicator('turbine')}
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <span className="font-mono text-[10px] font-medium text-slate-500 uppercase tracking-wider">Requester</span>
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('company')}
                    className="flex items-center font-mono text-[10px] font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700"
                  >
                    Company {renderSortIndicator('company')}
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort(statusDateColumn.key)}
                    className="flex items-center font-mono text-[10px] font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700"
                  >
                    {statusDateColumn.label} {renderSortIndicator(statusDateColumn.key)}
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('planned_end')}
                    className="flex items-center font-mono text-[10px] font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700"
                  >
                    Date Range {renderSortIndicator('planned_end')}
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('work_scope')}
                    className="flex items-center font-mono text-[10px] font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700"
                  >
                    Work Scope {renderSortIndicator('work_scope')}
                  </button>
                </th>
                <th className="px-4 py-3 text-center">
                  <span className="font-mono text-[10px] font-medium text-slate-500 uppercase tracking-wider">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm text-slate-500 font-medium">Loading work permits...</span>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <div className="text-sm text-red-500 font-medium">{error}</div>
                  </td>
                </tr>
              ) : paginatedWorkPermits.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                        <ClipboardList className="h-6 w-6 text-slate-400" />
                      </div>
                      <p className="text-sm font-black text-slate-900">No work permits found</p>
                      <p className="text-xs text-slate-500">
                        {searchTerm ? 'Try adjusting your search criteria' : `No ${config.label.toLowerCase()} work permits`}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedWorkPermits.map((wp) => (
                  <tr
                    key={wp.id}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                    onDoubleClick={() => handleViewWorkPermit(wp)}
                  >
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm font-medium text-slate-900">{wp.work_permit_number || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <FileText className="h-3 w-3 text-slate-400" />
                        <span className="text-sm text-slate-700">{wp.project_name || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm text-slate-600">{wp.turbine_name || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Users className="h-3 w-3 text-slate-400" />
                        <span className="text-sm text-slate-700">{getRequesterName(wp)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Building2 className="h-3 w-3 text-slate-400" />
                        <span className="text-sm text-slate-700">{wp.requesting_company_name || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-slate-500">{formatDateTime(wp[statusDateColumn.key])}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs text-slate-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(wp.planned_start)}
                        </div>
                        <div className="text-slate-400">→ {formatDate(wp.planned_end)}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-700 line-clamp-1">{wp.work_scope_name || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1.5">
                        {activeStatus === 'pending' ? (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); openActionModal('approve', wp); }}
                              className="px-2.5 py-1.5 bg-emerald-500 text-white rounded-lg text-[10px] font-black uppercase tracking-wide hover:bg-emerald-600 transition-colors"
                              title="Approve"
                            >
                              <CheckCircle className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); openActionModal('reject', wp); }}
                              className="px-2.5 py-1.5 bg-red-500 text-white rounded-lg text-[10px] font-black uppercase tracking-wide hover:bg-red-600 transition-colors"
                              title="Reject"
                            >
                              <XCircle className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleViewWorkPermit(wp); }}
                              className="p-1.5 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors"
                              title="View Details"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleViewWorkPermit(wp); }}
                            className="p-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && sortedWorkPermits.length > 0 && (
          <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between">
            <div className="text-xs text-slate-500 font-medium">
              Showing {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, sortedWorkPermits.length)} of {sortedWorkPermits.length} permits
            </div>
            <div className="flex items-center gap-1.5">
              {currentPage > 1 && (
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="h-8 px-3 flex items-center gap-1 text-[10px] font-black uppercase tracking-wide text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Prev
                </button>
              )}
              {getVisiblePages().map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`h-8 w-8 flex items-center justify-center text-[10px] font-black rounded-lg transition-colors ${
                    page === currentPage
                      ? 'bg-orange-500 text-white'
                      : 'text-slate-600 bg-slate-100 hover:bg-slate-200'
                  }`}
                >
                  {page}
                </button>
              ))}
              {currentPage < totalPages && (
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="h-8 px-3 flex items-center gap-1 text-[10px] font-black uppercase tracking-wide text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal - 3 Column Layout */}
      {detailModal.show && detailModal.permit && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0 bg-white">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg ${statusConfig[detailModal.permit.status]?.bg || 'bg-slate-100'}`}>
                  {(() => {
                    const Icon = statusConfig[detailModal.permit.status]?.icon || ClipboardList;
                    return <Icon className={`h-6 w-6 ${statusConfig[detailModal.permit.status]?.text || 'text-slate-600'}`} />;
                  })()}
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">
                      {detailModal.permit.work_permit_number}
                    </h3>
                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase ${statusConfig[detailModal.permit.status]?.badge || 'bg-slate-100 text-slate-600'}`}>
                      {statusConfig[detailModal.permit.status]?.label || detailModal.permit.status}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
                    Work Permit Review • {detailModal.permit.project_name}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {detailModal.permit.status === 'pending' && (
                  <>
                    <button
                      onClick={() => { closeDetailModal(); openActionModal('reject', detailModal.permit); }}
                      className="px-4 py-2.5 bg-red-50 text-red-600 font-black text-[10px] uppercase tracking-wide rounded-xl hover:bg-red-100 transition-colors flex items-center gap-1.5 border border-red-200"
                    >
                      <XCircle className="h-4 w-4" />
                      Reject
                    </button>
                    <button
                      onClick={() => { closeDetailModal(); openActionModal('approve', detailModal.permit); }}
                      className="px-4 py-2.5 bg-emerald-500 text-white font-black text-[10px] uppercase tracking-wide rounded-xl hover:bg-emerald-600 transition-colors flex items-center gap-1.5 shadow-lg shadow-emerald-500/20"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Approve
                    </button>
                  </>
                )}
                <button
                  onClick={closeDetailModal}
                  className="w-10 h-10 flex items-center justify-center bg-slate-100 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded-xl transition-all"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content - 3 Columns */}
            <div className="flex-1 flex overflow-hidden">
              {detailLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  {/* Left Column - Team Members */}
                  <div className="w-[320px] border-r border-slate-100 bg-slate-50/50 flex flex-col overflow-hidden">
                    {/* Work Permit Info */}
                    <div className="p-4 border-b border-slate-100 bg-white">
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-orange-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-black text-slate-900 truncate">{detailModal.permit.requesting_company_name}</p>
                            <p className="text-[10px] text-slate-500">{detailModal.permit.turbine_name}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="p-2 bg-slate-50 rounded-lg">
                            <p className="text-[9px] font-black text-slate-400 uppercase">Start</p>
                            <p className="text-[11px] font-bold text-slate-900">{formatDate(detailModal.permit.planned_start)}</p>
                          </div>
                          <div className="p-2 bg-slate-50 rounded-lg">
                            <p className="text-[9px] font-black text-slate-400 uppercase">End</p>
                            <p className="text-[11px] font-bold text-slate-900">{formatDate(detailModal.permit.planned_end)}</p>
                          </div>
                        </div>
                        <div className="p-2 bg-violet-50 rounded-lg border border-violet-200">
                          <p className="text-[9px] font-black text-violet-400 uppercase">Work Scope</p>
                          <p className="text-xs font-bold text-violet-900">{detailModal.permit.work_scope_name || '-'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Team Members Header */}
                    <div className="p-3 border-b border-slate-100 bg-amber-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-amber-600" />
                          <h4 className="text-[10px] font-black text-amber-900 uppercase tracking-widest">Team Members</h4>
                        </div>
                        <span className="text-[9px] font-black bg-amber-200 text-amber-800 px-2 py-0.5 rounded-md">
                          {detailModal.permit.team_data?.users?.length || 0}
                        </span>
                      </div>
                    </div>

                    {/* Team Members List */}
                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                      {detailModal.permit.team_data?.users?.slice().sort((a, b) => {
                        const roleA = getUserRole(a.id, detailModal.permit.team_data);
                        const roleB = getUserRole(b.id, detailModal.permit.team_data);
                        const priority = { 'Team Leader': 1, 'Supervisor': 2, 'Team Member': 3 };
                        return (priority[roleA] || 3) - (priority[roleB] || 3);
                      }).map((member, idx) => {
                        const isLeader = member.id === detailModal.permit.team_data?.team_leader;
                        const role = getUserRole(member.id, detailModal.permit.team_data);
                        const isSelected = selectedTeamMember?.id === member.id;
                        const validNominations = getValidNominations(member.id, detailModal.permit);
                        const hasValidNomination = validNominations.length > 0;

                        return (
                          <button
                            key={member.id || idx}
                            onClick={() => handleSelectTeamMember(member)}
                            className={`w-full text-left p-3 rounded-xl border transition-all ${
                              isSelected
                                ? 'bg-orange-500 border-orange-600 text-white shadow-lg shadow-orange-500/20'
                                : isLeader
                                  ? 'bg-orange-50 border-orange-200 hover:border-orange-300'
                                  : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {member.profile_picture ? (
                                <img
                                  src={member.profile_picture}
                                  alt=""
                                  className={`w-10 h-10 rounded-xl object-cover flex-shrink-0 ${isSelected ? 'ring-2 ring-white/50' : ''}`}
                                />
                              ) : (
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black flex-shrink-0 ${
                                  isSelected ? 'bg-white/20 text-white' : isLeader ? 'bg-orange-500 text-white' : 'bg-slate-200 text-slate-600'
                                }`}>
                                  {member.first_name?.charAt(0) || member.username?.charAt(0) || '?'}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className={`text-xs font-black truncate ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                                  {member.first_name || ''} {member.last_name || member.username || 'Unknown'}
                                </p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className={`text-[9px] font-bold uppercase ${
                                    isSelected ? 'text-white/70' : isLeader ? 'text-orange-600' : 'text-slate-500'
                                  }`}>
                                    {role}
                                  </span>
                                  {!isSelected && (
                                    hasValidNomination ? (
                                      <CheckCircle className="h-3 w-3 text-emerald-500" />
                                    ) : userNominations[member.id] !== undefined ? (
                                      <AlertCircle className="h-3 w-3 text-red-500" />
                                    ) : null
                                  )}
                                </div>
                              </div>
                              {isSelected && <ChevronRight className="h-4 w-4 text-white flex-shrink-0" />}
                            </div>
                          </button>
                        );
                      })}

                      {(!detailModal.permit.team_data?.users || detailModal.permit.team_data.users.length === 0) && (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                          <Users className="h-10 w-10 text-slate-300 mb-3" />
                          <p className="text-xs font-bold text-slate-500">No team members</p>
                        </div>
                      )}
                    </div>

                    {/* Status Info */}
                    {(detailModal.permit.status === 'rejected' || detailModal.permit.status === 'approved') && (
                      <div className={`p-3 border-t ${detailModal.permit.status === 'rejected' ? 'bg-red-50 border-red-100' : 'bg-emerald-50 border-emerald-100'}`}>
                        {detailModal.permit.status === 'rejected' && detailModal.permit.rejection_reason && (
                          <div>
                            <p className="text-[9px] font-black text-red-600 uppercase mb-1">Rejection Reason</p>
                            <p className="text-[10px] text-red-700 line-clamp-2">{detailModal.permit.rejection_reason}</p>
                          </div>
                        )}
                        {detailModal.permit.status === 'approved' && (
                          <div>
                            <p className="text-[9px] font-black text-emerald-600 uppercase mb-1">Approved</p>
                            <p className="text-[10px] text-emerald-700">{formatDateTime(detailModal.permit.approved_at)}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Middle Column - Documents */}
                  <div className={`${previewDocument ? 'w-[280px]' : 'flex-1'} border-r border-slate-100 flex flex-col overflow-hidden transition-all duration-300`}>
                    {selectedTeamMember ? (
                      <>
                        {/* Selected Member Header */}
                        <div className="p-4 border-b border-slate-100 bg-white">
                          <div className="flex items-center gap-3">
                            {selectedTeamMember.profile_picture ? (
                              <img
                                src={selectedTeamMember.profile_picture}
                                alt=""
                                className="w-12 h-12 rounded-xl object-cover shadow-lg"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-black text-lg shadow-lg">
                                {selectedTeamMember.first_name?.charAt(0) || '?'}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-black text-slate-900">
                                {selectedTeamMember.first_name} {selectedTeamMember.last_name}
                              </p>
                              <p className="text-[10px] text-orange-600 font-bold uppercase">
                                {getUserRole(selectedTeamMember.id, detailModal.permit.team_data)}
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mt-3">
                            <div className="flex items-center gap-2 text-[10px] text-slate-500">
                              <Mail className="h-3 w-3" />
                              <span className="truncate">{selectedTeamMember.email || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-[10px] text-slate-500">
                              <Phone className="h-3 w-3" />
                              <span>{selectedTeamMember.phone_number || 'N/A'}</span>
                            </div>
                          </div>
                        </div>

                        {/* Nominations */}
                        <div className="p-3 border-b border-slate-100 bg-emerald-50">
                          <div className="flex items-center gap-2 mb-2">
                            <Shield className="h-4 w-4 text-emerald-600" />
                            <h4 className="text-[10px] font-black text-emerald-900 uppercase tracking-widest">Nominations</h4>
                          </div>
                          {loadingNominations[selectedTeamMember.id] ? (
                            <div className="flex items-center gap-2 py-2">
                              <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                              <span className="text-[10px] text-emerald-600">Loading...</span>
                            </div>
                          ) : getValidNominations(selectedTeamMember.id, detailModal.permit).length > 0 ? (
                            <div className="space-y-1">
                              {getValidNominations(selectedTeamMember.id, detailModal.permit).map((nom) => (
                                <div key={nom.id} className="p-2 bg-white rounded-lg border border-emerald-200">
                                  <div className="flex items-center justify-between">
                                    <span
                                      className="px-2 py-0.5 text-[9px] font-black rounded"
                                      style={{
                                        backgroundColor: nom.authorization_level_color ? `${nom.authorization_level_color}20` : '#f1f5f9',
                                        color: nom.authorization_level_color || '#475569'
                                      }}
                                    >
                                      {nom.authorization_level_code || nom.authorization_level_name}
                                    </span>
                                    <span className="text-[9px] text-emerald-600 font-bold">
                                      → {formatDate(nom.date_end)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="p-2 bg-red-100 rounded-lg border border-red-200">
                              <div className="flex items-center gap-2">
                                <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                                <span className="text-[10px] text-red-700 font-bold">No valid nominations</span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Documents List */}
                        <div className="p-3 border-b border-slate-100 bg-blue-50">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-blue-600" />
                              <h4 className="text-[10px] font-black text-blue-900 uppercase tracking-widest">Documents</h4>
                            </div>
                            <span className="text-[9px] font-black bg-blue-200 text-blue-800 px-2 py-0.5 rounded-md">
                              {userQualifications[selectedTeamMember.id]?.length || 0}
                            </span>
                          </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-3">
                          {loadingQualifications[selectedTeamMember.id] ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                          ) : userQualifications[selectedTeamMember.id]?.length > 0 ? (
                            <div className="space-y-2">
                              {userQualifications[selectedTeamMember.id].map((qual) => {
                                const docStatus = getDocStatus(qual);
                                const qualName = qual.qualification_type_data?.name || qual.qualification_type_name || 'Document';
                                const isActive = previewDocument?.name === qualName;

                                return (
                                  <button
                                    key={qual.id}
                                    onClick={() => qual.certificate_file && handlePreview(qual.certificate_file, qualName)}
                                    disabled={!qual.certificate_file}
                                    className={`w-full text-left p-3 rounded-xl border transition-all ${
                                      isActive
                                        ? 'bg-orange-500 border-orange-600 text-white shadow-lg'
                                        : docStatus.status === 'expired'
                                          ? 'bg-red-50 border-l-4 border-l-red-500 border-red-200 hover:shadow-sm'
                                          : docStatus.status === 'expiring'
                                            ? 'bg-amber-50 border-l-4 border-l-amber-500 border-amber-200 hover:shadow-sm'
                                            : 'bg-white border-l-4 border-l-emerald-500 border-slate-200 hover:shadow-sm'
                                    } ${!qual.certificate_file ? 'opacity-60 cursor-not-allowed' : ''}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <FileText className={`h-4 w-4 flex-shrink-0 ${
                                        isActive ? 'text-white' :
                                        docStatus.status === 'expired' ? 'text-red-500' :
                                        docStatus.status === 'expiring' ? 'text-amber-500' : 'text-emerald-500'
                                      }`} />
                                      <div className="flex-1 min-w-0">
                                        <p className={`text-[11px] font-black uppercase truncate ${isActive ? 'text-white' : 'text-slate-800'}`}>
                                          {qualName}
                                        </p>
                                        <p className={`text-[9px] font-bold mt-0.5 ${isActive ? 'text-white/70' : 'text-slate-400'}`}>
                                          EXP: {qual.expiry_date ? formatDate(qual.expiry_date) : 'No expiry'}
                                        </p>
                                      </div>
                                      {!isActive && (
                                        <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded flex-shrink-0 ${
                                          docStatus.status === 'expired' ? 'bg-red-100 text-red-600' :
                                          docStatus.status === 'expiring' ? 'bg-amber-100 text-amber-600' :
                                          'bg-emerald-100 text-emerald-600'
                                        }`}>{docStatus.label}</span>
                                      )}
                                      {isActive && qual.certificate_file && <ChevronRight className="h-4 w-4 text-white flex-shrink-0" />}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center py-8 text-center">
                              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-3">
                                <AlertCircle className="h-6 w-6 text-amber-500" />
                              </div>
                              <p className="text-xs font-bold text-amber-700">No documents uploaded</p>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="flex-1 flex flex-col items-center justify-center text-slate-300 p-8">
                        <Users className="h-16 w-16 opacity-20 mb-4" />
                        <p className="text-sm font-black uppercase tracking-widest text-center">Select a team member</p>
                        <p className="text-xs text-slate-400 mt-2 text-center">Click on a team member to view their documents and nominations</p>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Document Preview */}
                  {previewDocument && (
                    <div className="flex-1 flex flex-col bg-slate-100">
                      {/* Preview Header */}
                      <div className="p-3 border-b border-slate-200 bg-white flex items-center justify-between">
                        <h4 className="font-bold text-slate-900 truncate flex-1 text-xs uppercase tracking-wide">{previewDocument.name}</h4>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <a
                            href={previewDocument.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 hover:text-orange-500 hover:border-orange-300 transition-colors"
                            title="Open in new tab"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                          <a
                            href={previewDocument.url}
                            download
                            className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 hover:text-orange-500 hover:border-orange-300 transition-colors"
                            title="Download"
                          >
                            <Download className="h-3.5 w-3.5" />
                          </a>
                          <button
                            onClick={() => setPreviewDocument(null)}
                            className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 hover:text-red-500 hover:border-red-300 transition-colors"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>

                      {/* Preview Content */}
                      <div className="flex-1 overflow-hidden p-4">
                        {previewDocument.type === 'pdf' ? (
                          <iframe
                            src={previewDocument.url}
                            className="w-full h-full border-0 rounded-xl bg-white shadow-lg"
                            title={previewDocument.name}
                          />
                        ) : previewDocument.type === 'image' ? (
                          <div className="w-full h-full flex items-center justify-center bg-slate-200 rounded-xl">
                            <img
                              src={previewDocument.url}
                              alt={previewDocument.name}
                              className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                            />
                          </div>
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center">
                            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                              <FileText className="h-8 w-8 text-slate-400" />
                            </div>
                            <p className="text-slate-600 mb-4 text-sm font-medium">Preview not available</p>
                            <a
                              href={previewDocument.url}
                              download
                              className="px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide transition-colors shadow-lg"
                            >
                              <Download className="h-4 w-4" />
                              Download
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Approve/Reject Modal */}
      {actionModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-5 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    actionModal.type === 'approve' ? 'bg-emerald-100' : 'bg-red-100'
                  }`}>
                    {actionModal.type === 'approve'
                      ? <CheckCircle className="h-5 w-5 text-emerald-600" />
                      : <XCircle className="h-5 w-5 text-red-600" />
                    }
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">
                      {actionModal.type === 'approve' ? 'Approve' : 'Reject'} Work Permit
                    </h3>
                    <p className="text-[10px] text-slate-500 font-medium">
                      {actionModal.permit?.work_permit_number}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeActionModal}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {/* Permit details summary */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2 text-xs">
                  <Building2 className="h-3.5 w-3.5 text-slate-400" />
                  <span className="font-medium text-slate-600">{actionModal.permit?.requesting_company_name}</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <FileText className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-slate-600">{actionModal.permit?.project_name}</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <Users className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-slate-600">{getRequesterName(actionModal.permit)}</span>
                </div>
                <div className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-200">
                  <strong>Work Scope:</strong> {actionModal.permit?.work_scope_name}
                </div>
              </div>

              {/* Rejection reason (only for reject) */}
              {actionModal.type === 'reject' && (
                <div>
                  <label className="block text-[10px] font-black text-slate-700 uppercase tracking-wider mb-1.5">
                    Rejection Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Please provide a reason for rejection..."
                    className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-300 resize-none"
                    rows={3}
                    required
                  />
                </div>
              )}

              {/* OEM Comments (optional) */}
              <div>
                <label className="block text-[10px] font-black text-slate-700 uppercase tracking-wider mb-1.5">
                  <MessageSquare className="h-3 w-3 inline mr-1" />
                  OEM Comments (Optional)
                </label>
                <textarea
                  value={oemComments}
                  onChange={(e) => setOemComments(e.target.value)}
                  placeholder="Add any additional comments..."
                  className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-300 resize-none"
                  rows={2}
                />
              </div>
            </div>

            <div className="p-5 border-t border-slate-100 flex items-center justify-end gap-2">
              <button
                onClick={closeActionModal}
                className="px-4 py-2 text-slate-600 font-black text-[10px] uppercase tracking-wide hover:bg-slate-100 rounded-lg transition-colors"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={actionModal.type === 'approve' ? handleApprove : handleReject}
                disabled={actionLoading || (actionModal.type === 'reject' && !rejectReason.trim())}
                className={`px-5 py-2 font-black text-[10px] uppercase tracking-wide rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed ${
                  actionModal.type === 'approve'
                    ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                {actionLoading ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    {actionModal.type === 'approve' ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                    {actionModal.type === 'approve' ? 'Approve' : 'Reject'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OEMWorkPermits;
